import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, SectionHeader } from "./Reveal";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { full_name: "", company: "", email: "", phone: "", brand_interest: "", application_details: "" };

const inputCls =
  "w-full border border-white/10 bg-ink px-4 py-3 text-[15px] text-white placeholder:text-slate-600 outline-none transition-colors focus:border-steel/70";
const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400";

export default function InquiryForm() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const analyze = async () => {
    if (analyzing || form.application_details.trim().length < 10) return;
    setAnalyzing(true);
    try {
      const res = await axios.post(
        `${API}/ai/analyze`,
        {
          application_details: form.application_details,
          brand_interest: form.brand_interest,
          lang,
        },
        { timeout: 90000 }
      );
      setAnalysis(res.data.analysis || "");
    } catch (err) {
      toast.error(t.ai.error);
    } finally {
      setAnalyzing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(
        `${API}/leads`,
        { ...form, ai_analysis: analysis || undefined },
        { timeout: 15000 }
      );
      setDone(true);
      toast.success(t.form.successTitle, { description: t.form.successBody });
      setForm(EMPTY);
      setAnalysis(null);
    } catch (err) {
      toast.error(t.form.errorTitle, { description: t.form.errorBody });
    } finally {
      setSending(false);
    }
  };

  const contactCards = [
    {
      icon: "fa-phone-volume",
      label: t.form.contactLabels.phone,
      lines: ["+91 95946 40444", "+91 88288 18444"],
      hrefs: ["tel:+919594640444", "tel:+918828818444"],
      testid: "contact-phone",
    },
    {
      icon: "fa-envelope",
      label: t.form.contactLabels.email,
      lines: ["md@teknik.net.in"],
      hrefs: ["mailto:md@teknik.net.in"],
      testid: "contact-email",
    },
    {
      icon: "fa-location-dot",
      label: t.form.contactLabels.visit,
      lines: [t.form.address],
      hrefs: [],
      testid: "contact-address",
    },
  ];

  return (
    <section id="contact" className="py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.15fr] lg:gap-20">
        <div>
          <SectionHeader eyebrow={t.form.eyebrow} title={t.form.title} sub={t.form.sub} />

          <div className="mt-10 space-y-4">
            {contactCards.map((c, i) => (
              <Reveal key={i} delay={0.1 + i * 0.08}>
                <div className="flex items-start gap-4 border border-white/10 bg-deep p-5" data-testid={c.testid}>
                  <span className="grid h-11 w-11 shrink-0 place-items-center border border-white/10 bg-panel/50 text-steel">
                    <i className={`fa-solid ${c.icon}`} />
                  </span>
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{c.label}</div>
                    <div className="mt-1.5 space-y-0.5">
                      {c.lines.map((line, j) =>
                        c.hrefs[j] ? (
                          <a
                            key={j}
                            href={c.hrefs[j]}
                            data-testid={`${c.testid}-link-${j}`}
                            className="block text-[15px] font-medium text-white transition-colors hover:text-steel"
                          >
                            {line}
                          </a>
                        ) : (
                          <p key={j} className="text-[15px] font-medium leading-snug text-white">
                            {line}
                          </p>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="lead-form"
            className="clip-frame-sm border border-white/10 bg-deep p-6 sm:p-10"
          >
            {done && (
              <div
                data-testid="lead-success"
                className="mb-6 flex items-center gap-3 border border-steel/40 bg-steel/10 px-4 py-3 text-sm text-steel"
              >
                <i className="fa-solid fa-circle-check" />
                {t.form.successTitle} — {t.form.successBody}
              </div>
            )}

            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className={labelCls} htmlFor="lead-name">{t.form.labels.name} *</label>
                <input
                  id="lead-name"
                  data-testid="lead-name"
                  className={inputCls}
                  required
                  minLength={2}
                  value={form.full_name}
                  onChange={set("full_name")}
                  placeholder={t.form.placeholders.name}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="lead-company">{t.form.labels.company} *</label>
                <input
                  id="lead-company"
                  data-testid="lead-company"
                  className={inputCls}
                  required
                  minLength={2}
                  value={form.company}
                  onChange={set("company")}
                  placeholder={t.form.placeholders.company}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="lead-email">{t.form.labels.email} *</label>
                <input
                  id="lead-email"
                  data-testid="lead-email"
                  type="email"
                  className={inputCls}
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder={t.form.placeholders.email}
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="lead-phone">{t.form.labels.phone} *</label>
                <input
                  id="lead-phone"
                  data-testid="lead-phone"
                  type="tel"
                  className={inputCls}
                  required
                  minLength={6}
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder={t.form.placeholders.phone}
                />
              </div>
            </div>

            <div className="mt-5">
              <label className={labelCls} htmlFor="lead-brand">{t.form.labels.brand} *</label>
              <div className="relative">
                <select
                  id="lead-brand"
                  data-testid="lead-brand"
                  className={`${inputCls} appearance-none pr-10 ${form.brand_interest ? "text-white" : "text-slate-500"}`}
                  required
                  value={form.brand_interest}
                  onChange={set("brand_interest")}
                >
                  <option value="" disabled>
                    {t.form.selectOption}
                  </option>
                  {t.form.brandOptions.map((o) => (
                    <option key={o} value={o} className="bg-ink text-white">
                      {o}
                    </option>
                  ))}
                </select>
                <i className="fa-solid fa-chevron-down pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-500" />
              </div>
            </div>

            <div className="mt-5">
              <label className={labelCls} htmlFor="lead-details">{t.form.labels.details} *</label>
              <textarea
                id="lead-details"
                data-testid="lead-details"
                className={`${inputCls} min-h-[120px] resize-y`}
                required
                minLength={5}
                value={form.application_details}
                onChange={set("application_details")}
                placeholder={t.form.placeholders.details}
              />
              <div className="mt-3">
                <button
                  type="button"
                  data-testid="ai-analyze-btn"
                  onClick={analyze}
                  disabled={analyzing || form.application_details.trim().length < 10}
                  className="inline-flex items-center gap-2 border border-steel/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-steel transition-colors hover:bg-steel/10 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {analyzing ? (
                    <>
                      <i className="fa-solid fa-spinner animate-spin" />
                      {t.ai.thinking}
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-wand-magic-sparkles" />
                      {t.ai.analysisBtn}
                    </>
                  )}
                </button>
                {analysis !== null && (
                  <div data-testid="ai-analysis-card" className="mt-3 border border-steel/40 bg-steel/10 p-4">
                    <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-steel">
                      <i className="fa-solid fa-microchip" />
                      {t.ai.analysisTitle}
                    </div>
                    <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-200">{analysis}</p>
                    <p className="mt-2 text-[11px] text-slate-500">{t.ai.analysisDisclaimer}</p>
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              data-testid="lead-submit"
              disabled={sending}
              className="mt-8 inline-flex w-full items-center justify-center gap-3 bg-crimson px-8 py-4 text-sm font-semibold uppercase tracking-wide text-white transition-colors hover:bg-crimson-hover disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? (
                <>
                  <i className="fa-solid fa-spinner animate-spin" />
                  {t.form.submitting}
                </>
              ) : (
                <>
                  {t.form.submit}
                  <i className="fa-solid fa-arrow-right" />
                </>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
